// Global Navigation Component
// Shared across Dashboard, Edit Profile, and other authenticated pages

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, Search, MessageCircle, Mail, Sparkles, ShoppingCart, Settings, Users, Gamepad2
} from 'lucide-react';
import { AuthContext } from '../App';
import apiClient from "../api/client";
import { ThemeToggleCompact } from './ThemeToggle';
import { useArcadeInviteBadge } from '../lib/arcade/useArcadeInviteBadge';


export function GlobalNav() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  
  const arcadeBadge = useArcadeInviteBadge({ enabled: !!token });
  
  // 🔧 FIX: State for settings dropdown
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // 🔧 FIX: Use notification summary for accurate badge counts
  const [badgeCounts, setBadgeCounts] = useState({
    matches_unread: 0,
    likes_unread: 0,
    messages_unread: 0,
    flirts_unread: 0,
  });

  // P3: Incoming connection requests count for badge
  const [incomingPendingCount, setIncomingPendingCount] = useState(0);

  // Fetch notification summary
  const fetchBadgeCounts = useCallback(async () => {
    if (!token) return;

    try {
      const res = await apiClient.get(`/notifications/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBadgeCounts({
        matches_unread: res.data.matches_unread || 0,
        likes_unread: res.data.likes_unread || 0,
        messages_unread: res.data.messages_unread || 0,
        flirts_unread: res.data.flirts_unread || 0,
      });
    } catch (err) {
      console.error('[GlobalNav] Error fetching badge counts:', err);
    }
  }, [token]);

  // P3: Fetch incoming connection requests count
  const fetchIncomingCount = useCallback(async () => {
    if (!token) return;

    try {
      const res = await apiClient.get(`/connections/me/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Count pending incoming requests
      const incoming = res.data?.incoming_requests || [];
      const pendingCount = incoming.filter(r => !r.status || r.status === "pending").length;
      setIncomingPendingCount(pendingCount);
    } catch (err) {
      // Silently fail - don't spam console for non-critical badge
      if (err?.response?.status !== 401) {
        console.warn('[GlobalNav] Could not fetch incoming count');
      }
    }
  }, [token]);

  useEffect(() => {
    fetchBadgeCounts();
    fetchIncomingCount();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchBadgeCounts();
      fetchIncomingCount();
    }, 30000);
    
    // 🔧 FIX: Register as impl so App.js wrapper can call it
    window.refreshNotificationSummaryImpl = fetchBadgeCounts;
    
    // P3: Allow external refresh of incoming count
    window.refreshIncomingCount = fetchIncomingCount;
    
    // 🚀 Direct state update (faster than re-fetch)
    window.updateBadgeCounts = (summary) => {
      setBadgeCounts({
        matches_unread: summary.matches_unread || 0,
        likes_unread: summary.likes_unread || 0,
        messages_unread: summary.messages_unread || 0,
        flirts_unread: summary.flirts_unread || 0,
      });
    };
    
    // P3: Direct update for incoming count
    window.updateIncomingCount = (count) => setIncomingPendingCount(count);
    
    return () => {
      clearInterval(interval);
      window.refreshNotificationSummaryImpl = null;
      window.updateBadgeCounts = null;
      window.refreshIncomingCount = null;
      window.updateIncomingCount = null;
    };
  }, [fetchBadgeCounts, fetchIncomingCount]);

  // 🔧 FIX: Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setSettingsOpen(false);
    
    if (settingsOpen) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [settingsOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 surface-raised backdrop-blur-sm z-50" style={{ paddingTop: 'var(--safe-area-inset-top, 0px)' }}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo - truncate on very small screens */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition flex-shrink-0 min-w-0"
          >
            <Heart className="w-6 h-6 sm:w-7 sm:h-7 text-rose-600 fill-current flex-shrink-0" />
            <span className="font-playfair text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 truncate">
              Unity Love
            </span>
          </button>
          
          {/* Navigation Icons - prevent overflow, ensure tap targets */}
          <div className="flex items-center gap-1 sm:gap-3 md:gap-5 flex-shrink-0">
            <button
              onClick={() => navigate('/discover')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 transition rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20"
              title="Discover"
              aria-label="Discover profiles"
            >
              <Search className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Discover</span>
            </button>

            <button
              onClick={() => navigate('/arcade')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-violet-600 dark:hover:text-violet-400 transition rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 relative"
              title="Connection Arcade"
              aria-label="Connection Arcade"
            >
              <Gamepad2 className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Arcade</span>
              {arcadeBadge.hasBadge && (
                <span className="absolute -top-0.5 -right-0.5 sm:relative sm:top-auto sm:right-auto sm:ml-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold leading-none">
                  {arcadeBadge.label}
                </span>
              )}
            </button>
            
            <button
              onClick={() => navigate('/matches')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 relative transition rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20"
              title="Matches"
              aria-label={badgeCounts.matches_unread > 0 ? `Matches - ${badgeCounts.matches_unread} unread` : 'Matches'}
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Matches</span>
              {badgeCounts.matches_unread > 0 && (
                <span className="absolute -top-1 -right-1 sm:top-0 sm:right-0 bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" aria-hidden="true">
                  {badgeCounts.matches_unread > 9 ? '9+' : badgeCounts.matches_unread}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate('/inbox')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 relative transition rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20"
              title="Messages"
              aria-label={badgeCounts.messages_unread > 0 ? `Messages - ${badgeCounts.messages_unread} unread` : 'Messages'}
            >
              <Mail className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Messages</span>
              {badgeCounts.messages_unread > 0 && (
                <span className="absolute -top-1 -right-1 sm:top-0 sm:right-0 bg-blue-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" aria-hidden="true">
                  {badgeCounts.messages_unread > 9 ? '9+' : badgeCounts.messages_unread}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate('/wingman')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 relative transition rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20"
              title="AI Wingman"
              aria-label="AI Wingman"
            >
              <Sparkles className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Wingman</span>
            </button>

            <button
              onClick={() => navigate('/shop')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-pink-600 dark:hover:text-pink-400 transition rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/20"
              title="Shop"
              aria-label="Shop"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Shop</span>
            </button>

            <button
              onClick={() => navigate('/connections')}
              className="flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-2 sm:py-1.5 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 relative transition rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              title="Connections"
              aria-label={incomingPendingCount > 0 ? `Connections - ${incomingPendingCount} pending requests` : 'Connections - multi-mode networking'}
            >
              <Users className="w-5 h-5" />
              <span className="hidden sm:block ml-1.5 text-sm">Connect</span>
              {incomingPendingCount > 0 && (
                <span className="absolute -top-1 -right-1 sm:top-0 sm:right-0 bg-indigo-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" aria-hidden="true">
                  {incomingPendingCount > 9 ? '9+' : incomingPendingCount}
                </span>
              )}
            </button>

            {/* Settings Dropdown with Admin Access */}
            <div className="relative flex items-center gap-2">
              {/* Theme Toggle - Always visible */}
              <ThemeToggleCompact />
              
              {/* Settings Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSettingsOpen(prev => !prev);
                }}
                className="flex items-center justify-center w-10 h-10 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50"
                title="Settings"
                aria-label="Settings menu"
                aria-expanded={settingsOpen}
                aria-haspopup="true"
              >
                <Settings className="w-5 h-5" />
              </button>
              
              {settingsOpen && (
                <div 
                  className="absolute right-0 top-full mt-2 w-48 surface-raised rounded-lg py-2 z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsOpen(false);
                      navigate('/edit-profile');
                    }}
                    className="block px-4 py-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 w-full text-left"
                  >
                    Edit Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsOpen(false);
                      navigate('/settings');
                    }}
                    className="block px-4 py-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 w-full text-left"
                  >
                    Settings
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsOpen(false);
                      navigate('/connections');
                    }}
                    className="block px-4 py-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 w-full text-left"
                  >
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-indigo-500" />
                      Connections
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 block">Manage intent & requests</span>
                  </button>
                  
                  {/* Admin Dashboard - Only for admin users */}
                  {user?.is_admin && (
                    <button
                      type="button"
                      onClick={() => {
                        console.log('[GlobalNav] Admin Dashboard clicked, navigating to /admin');
                        setSettingsOpen(false);
                        navigate('/admin');
                      }}
                      className="block px-4 py-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 w-full text-left border-t border-slate-200 dark:border-slate-700 font-medium"
                      data-testid="admin-nav-btn"
                    >
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 mr-2" />
                      Admin Dashboard
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
